import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import clsx from 'clsx';
import { isFunction } from '@dxb-web-ui-framework/common/utils/Helper';
import React, { useCallback, useEffect, useState, VFC } from 'react';
import { Box, makeStyles } from '@dxb-web-ui-framework/design-system';
import { AuthorizationsRepositoryImpl } from '../../../../../../iam/src/domain/Authorizations/infrastructure';
import { AuthorizationsServiceImpl } from '../../../../../../iam/src/domain/Authorizations/usecases';
import {
  useWizardErrorState as useClientSideWizardErrorState,
  useWizardGoToNextStep,
  useWizardStepData,
  useWizardCurrentProcessState,
} from '../../../../contexts/ClientSideWizard/hooks';
import {
  useSetWizardErrorState as useSetProcessEngineWizardErrorState,
  useWizardErrorState as useProcessEngineWizardErrorState,
} from '../../../../contexts/ProcessEngineWizard/hooks';
import { OTP, SCA } from './InApp2FAMethods';

const getAuthorizationService = () => {
  const authorizationsRepo = new AuthorizationsRepositoryImpl();
  return new AuthorizationsServiceImpl(authorizationsRepo);
};

const useStyles = makeStyles({
  hasOverflowYAuto: {
    overflowY: 'auto',
  },
});

export enum AuthenticationMethodEnum {
  OTP = 'OTP',
  SCA = 'SCA',
}

export type stepDataPropsValues = {
  authenticationMethod: AuthenticationMethodEnum;
  authorizationId: Nullable<number>;
  onCancel?: () => void;
  onSuccess?: (data: any) => void;
};

export interface InAPP2FAStepProps {
  stepId?: string;
  authenticationMethod?: AuthenticationMethodEnum;
  title?: string;
  mobile?: string;
  caption?: string;
  subtitle?: string;
  cancelable?: boolean;
  hasOverflowYAuto?: Boolean;
  resetFlow?: () => void;
  onToggleDrawer?: Function;
  isLeftPositioned?: boolean;
  shouldEnableOtpTitle?: boolean;
  shouldForceMobileOtpView?: boolean;
  externalAuthorizationId?: null | number;
  externalOnGoToNextWizardStep?: (data: any) => void;
  externalOnCancel?: () => void;
  stepDataPropsValues?: {
    values: stepDataPropsValues;
  };
}

const InAPP2FAStep: VFC<InAPP2FAStepProps> = ({
  stepId,
  authenticationMethod,
  title,
  mobile,
  caption,
  subtitle,
  cancelable,
  hasOverflowYAuto,
  resetFlow,
  onToggleDrawer,
  isLeftPositioned = false,
  shouldEnableOtpTitle = true,
  shouldForceMobileOtpView = false,
  stepDataPropsValues,
  externalAuthorizationId,
  externalOnGoToNextWizardStep,
  externalOnCancel,
}) => {
  //Client side wizard hooks
  const currentState = useWizardCurrentProcessState();
  const stepDataValues = useWizardStepData(
    stepDataPropsValues ?? stepId ?? currentState.formData?.previousStep,
  ) as GenericObject;

  const goToNextStep = useWizardGoToNextStep();
  const clientSideWizardErrorState = useClientSideWizardErrorState();
  const processEngineWizardErrorState = useProcessEngineWizardErrorState();
  const setProcessEngineWizardErrorState = useSetProcessEngineWizardErrorState();

  // Authorizations states
  const [authId, setAuthId] = useState<Nullable<number>>(null);
  const [authMethod, setAuthMethod] = useState(stepDataValues?.values?.authenticationMethod);

  //Authorization Service
  const authorizationsService = getAuthorizationService();

  useEffect(() => {
    if (authenticationMethod) {
      setAuthMethod(authenticationMethod);
    }
  }, [authenticationMethod]);

  // setAuthId from previous step (client side wizard)
  useEffect(() => {
    if (stepDataValues) {
      setAuthId(stepDataValues?.values?.authorizationId);
    }
  }, [stepDataValues]);

  // setAuthId from response (process engine wizard)
  useEffect(() => {
    if (externalAuthorizationId) {
      setAuthId(externalAuthorizationId);
    }
  }, [externalAuthorizationId]);

  useEffect(() => {
    if (authMethod === null) {
      setTimeout(() => goToNextStep({}), 1);
    }
  }, [authMethod]);

  // Error and Success callbacks for OTP
  const onError = useCallback(
    data => {
      if (!setProcessEngineWizardErrorState) {
        clientSideWizardErrorState.setNetworkCallErrors(data);
        return;
      }
      setProcessEngineWizardErrorState(data);
    },
    [clientSideWizardErrorState, setProcessEngineWizardErrorState],
  );

  const onSuccess = useCallback(
    (data: any) => {
      if (stepDataValues && stepDataValues?.values?.onSuccess && isFunction(stepDataValues?.values?.onSuccess)) {
        stepDataValues?.values?.onSuccess(data);
      }

      if (stepDataPropsValues?.values) {
        return;
      }
      if (externalOnGoToNextWizardStep && isFunction(externalOnGoToNextWizardStep)) {
        externalOnGoToNextWizardStep(data);
        return;
      }
      goToNextStep({});
    },
    [goToNextStep, stepDataValues, stepDataPropsValues, externalOnGoToNextWizardStep],
  );

  // SCA change AuthMethod
  const changeAuthorizationId = useCallback((id: number) => {
    setAuthMethod((prev: string) => (prev === 'OTP' ? 'SCA' : 'OTP'));
    setAuthId(id);
  }, []);

  const onCancel = useCallback(() => {
    if (stepDataValues && stepDataValues?.values?.onCancel && isFunction(stepDataValues?.values?.onCancel)) {
      stepDataValues?.values?.onCancel();
    }
    if (externalOnCancel && isFunction(externalOnCancel)) {
      externalOnCancel();
    }
    if (clientSideWizardErrorState.errors) {
      clientSideWizardErrorState.setNetworkCallErrors(null);
    }
    if (processEngineWizardErrorState && setProcessEngineWizardErrorState) {
      setProcessEngineWizardErrorState(null);
    }
  }, [
    stepDataValues,
    externalOnCancel,
    clientSideWizardErrorState,
    processEngineWizardErrorState,
    setProcessEngineWizardErrorState,
  ]);

  const classes = useStyles();

  if (!authId) {
    return null;
  }

  return (
    <Box
      display='flex'
      paddingTop={1}
      paddingBottom={6}
      justifyContent={isLeftPositioned ? 'left' : 'center'}
      alignItems='center'
      flexGrow={1}
      className={clsx({ [classes.hasOverflowYAuto]: Boolean(hasOverflowYAuto) })}>
      <>
        {authMethod === AuthenticationMethodEnum.SCA ? (
          <SCA
            authorizationsService={authorizationsService}
            authMethod={authMethod}
            authorizationId={authId}
            changeAuthorizationId={changeAuthorizationId}
            title={title}
            resetFlow={resetFlow}
            isLeftPositioned={isLeftPositioned}
            onToggleDrawer={onToggleDrawer}
            externalOnGoToNextWizardStep={externalOnGoToNextWizardStep}
            externalErrors={processEngineWizardErrorState}
            externalErrorSetter={setProcessEngineWizardErrorState}
          />
        ) : (
          <OTP
            authorizationId={authId}
            authorizationsService={authorizationsService}
            onCancel={onCancel}
            onError={onError}
            onSuccess={onSuccess}
            cancelable={cancelable}
            previousStepsValues={stepDataValues?.values}
            title={title}
            subtitle={subtitle}
            caption={caption}
            mobile={mobile}
            onToggleDrawer={onToggleDrawer}
            shouldEnableOtpTitle={shouldEnableOtpTitle}
            shouldForceMobileOtpView={shouldForceMobileOtpView}
          />
        )}
      </>
    </Box>
  );
};

export { InAPP2FAStep };
